// ===========================================
// Dev Chat Page - Global Developer Chat
// ===========================================
// Universal chat for devs to talk shop about building, research, and industry news

import React, { useState, useEffect, useRef, useCallback } from 'react';
// Note: useRef is used for:
// 1. messagesEndRef - auto-scroll to latest message
// 2. messagesContainerRef - message container reference
// 3. isMountedRef - track component mount state to prevent memory leaks
import { useAuth } from '../context/AuthContext';
import { chatAPI } from '../services/api';
import {
  Send,
  Hash,
  Users,
  Code2,
  FolderKanban,
  Newspaper,
  Lightbulb,       // Feature Requests channel icon
  HelpCircle,      // Support channel icon
  Loader
} from 'lucide-react';
import './DevChat.scss';

// ===========================================
// Room Configuration
// ===========================================
// Defines available chat rooms with icons and descriptions
const ROOMS = {
  general: { 
    label: 'General', 
    icon: Hash, 
    color: '#22c55e',
    description: 'Casual chat & community vibes'
  },
  dev: { 
    label: 'Dev', 
    icon: Code2, 
    color: '#3b82f6',
    description: 'Technical discussions & debugging'
  },
  projects: { 
    label: 'Projects', 
    icon: FolderKanban, 
    color: '#a855f7',
    description: 'Share what you\'re building'
  },
  news: { 
    label: 'News', 
    icon: Newspaper, 
    color: '#f59e0b',
    description: 'Weekly updates & industry news'
  },
  // Feature Requests - users can suggest and discuss new features
  features: { 
    label: 'Feature Requests', 
    icon: Lightbulb, 
    color: '#06b6d4',
    description: 'Request & discuss new features'
  },
  // Support - users can get help and troubleshoot issues
  support: { 
    label: 'Support', 
    icon: HelpCircle, 
    color: '#ef4444',
    description: 'Get help & troubleshoot issues'
  }
};

const DevChat = () => {
  // ===========================================
  // State Management
  // ===========================================
  const { user } = useAuth();
  const [activeRoom, setActiveRoom] = useState('general');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [onlineCount, setOnlineCount] = useState(0);
  // Unread message counts per room
  const [unreadCounts, setUnreadCounts] = useState({});
  
  // Refs for auto-scroll
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // ===========================================
  // Fetch Messages for Active Room
  // ===========================================
  // Use ref to track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  
  const fetchMessages = useCallback(async () => {
    try {
      const response = await chatAPI.getMessages(activeRoom);
      // Only update state if component is still mounted
      if (isMountedRef.current && response.data.success) {
        setMessages(response.data.data.messages || []);
        setOnlineCount(response.data.data.onlineCount || 0);
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    }
    if (isMountedRef.current) {
      setLoading(false);
    }
  }, [activeRoom]);

  // Fetch on mount and room change
  useEffect(() => {
    isMountedRef.current = true;
    setLoading(true);
    fetchMessages();
    
    // Poll for new messages every 5 seconds
    const interval = setInterval(fetchMessages, 5000);
    return () => {
      isMountedRef.current = false;  // Mark as unmounted
      clearInterval(interval);
    };
  }, [fetchMessages]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ===========================================
  // Unread Message Count Tracking
  // ===========================================
  // Fetch unread counts for all rooms on mount and periodically
  useEffect(() => {
    const fetchUnreadCounts = async () => {
      try {
        const response = await chatAPI.getUnreadCounts();
        if (response.data.success) {
          setUnreadCounts(response.data.data);
        }
      } catch (err) {
        console.error('Failed to fetch unread counts:', err);
      }
    };
    
    fetchUnreadCounts();
    // Refresh unread counts every 30 seconds
    const interval = setInterval(fetchUnreadCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  // Mark room as read when user switches to it
  useEffect(() => {
    const markAsRead = async () => {
      try {
        await chatAPI.markRoomAsRead(activeRoom);
        // Clear unread count for active room locally
        setUnreadCounts(prev => ({ ...prev, [activeRoom]: 0 }));
      } catch (err) {
        console.error('Failed to mark room as read:', err);
      }
    };
    
    markAsRead();
  }, [activeRoom]);

  // ===========================================
  // Send Message Handler
  // ===========================================
  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const response = await chatAPI.sendMessage({
        room: activeRoom,
        content: newMessage.trim()
      });
      
      if (response.data.success) {
        // Add message to list immediately
        setMessages(prev => [...prev, response.data.data]);
        setNewMessage('');
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    }
    setSending(false);
  };

  // ===========================================
  // Format timestamp
  // ===========================================
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format date for grouping
  const formatDate = (date) => {
    const d = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, msg) => {
    const date = formatDate(msg.createdAt);
    if (!groups[date]) groups[date] = [];
    groups[date].push(msg);
    return groups;
  }, {});

  // ===========================================
  // Render
  // ===========================================
  return (
    <div className="dev-chat">
      {/* Header - Tactical Terminal Style */}
      <div className="dev-chat__header">
        <div className="dev-chat__header-info">
          <h1>Dev Chat</h1>
          <p>Talk shop with fellow developers</p>
        </div>
        <div className="dev-chat__header-stats">
          <Users size={12} />
          <span>{onlineCount} active</span>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="dev-chat__container">
        {/* Room Sidebar */}
        <aside className="dev-chat__rooms">
          <h3>Rooms</h3>
          <ul className="dev-chat__room-list">
            {Object.entries(ROOMS).map(([key, room]) => {
              const Icon = room.icon;
              const unreadCount = unreadCounts[key] || 0;
              return (
                <li key={key}>
                  <button
                    className={`dev-chat__room-btn ${activeRoom === key ? 'active' : ''}`}
                    onClick={() => setActiveRoom(key)}
                  >
                    <Icon size={10} style={{ color: activeRoom === key ? room.color : undefined }} />
                    <span className="dev-chat__room-label">{room.label}</span>
                    {/* Unread message count badge - only show when there are unread messages */}
                    {unreadCount > 0 && (
                      <span className="dev-chat__unread-badge">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          
          {/* Room Description */}
          <div className="dev-chat__room-info">
            <div 
              className="dev-chat__room-indicator"
              style={{ background: ROOMS[activeRoom].color }}
            />
            <p>{ROOMS[activeRoom].description}</p>
          </div>
        </aside>

        {/* Messages Panel */}
        <main className="dev-chat__main">
          {/* Messages Container */}
          <div className="dev-chat__messages" ref={messagesContainerRef}>
            {loading ? (
              <div className="dev-chat__loading">
                <Loader className="animate-spin" size={20} />
                <span>Loading...</span>
              </div>
            ) : messages.length === 0 ? (
              <div className="dev-chat__empty">
                <Hash size={32} style={{ color: ROOMS[activeRoom].color }} />
                <h3>No messages in #{activeRoom}</h3>
                <p>Start the conversation!</p>
              </div>
            ) : (
              Object.entries(groupedMessages).map(([date, msgs]) => (
                <div key={date} className="dev-chat__date-group">
                  <div className="dev-chat__date-divider">
                    <span>{date}</span>
                  </div>
                  {msgs.map((msg) => (
                    <div 
                      key={msg._id} 
                      // Note: Optional chaining on msg.user in case the user was deleted
                      className={`dev-chat__message ${msg.user?._id === user?._id ? 'own' : ''}`}
                    >
                      <div className="dev-chat__message-avatar">
                        {/* Note: Fallback to 'U' if user is null or name is missing */}
                        {msg.user?.name?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      <div className="dev-chat__message-content">
                        <div className="dev-chat__message-header">
                          {/* Note: Show 'Deleted User' if user was removed */}
                          <span className="dev-chat__message-author">{msg.user?.name || 'Deleted User'}</span>
                          <span className="dev-chat__message-time">{formatTime(msg.createdAt)}</span>
                        </div>
                        <p className="dev-chat__message-text">{msg.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <form className="dev-chat__input-form" onSubmit={handleSend}>
            <input
              type="text"
              placeholder={`Message #${activeRoom}...`}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              maxLength={1000}
              disabled={sending}
            />
            <button 
              type="submit" 
              className="btn-primary"
              disabled={!newMessage.trim() || sending}
            >
              {sending ? <Loader size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </form>
        </main>
      </div>
    </div>
  );
};

export default DevChat;

